/**
 * QorTrace · Homepage social-proof showcase (Iter204).
 *
 * Lists customers who have verified their domain via
 * `/.well-known/qortrace.json` and published a public PQC threat
 * radar. Each entry shows the logo + domain + a deep-link to the
 * customer's public radar.
 *
 * Renders nothing until there's at least 1 verified customer — better
 * empty space than a fake "trusted by" claim.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Radar, ArrowRight } from "lucide-react";


const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: API });


export default function CustomerRadarShowcase() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    client.get("/api/public/customer-radars/showcase?limit=24")
      .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <section
      className="max-w-[1400px] mx-auto px-6 sm:px-8 py-12"
      data-testid="customer-radar-showcase"
    >
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="qt-label" style={{ color: "var(--qt-cyan)", letterSpacing: "0.16em" }}>
            <Radar size={11} style={{ display: "inline", marginRight: 6 }} />
            ORGS PUBLISHING THEIR PQC THREAT RADAR
          </div>
          <h2
            className="qt-h2 mt-2"
            style={{ fontSize: "1.8rem", color: "#fff", lineHeight: 1.2 }}
          >
            {items.length}+ orgs publish their PQC threat radar via QorTrace
          </h2>
          <p className="mt-2" style={{ color: "var(--qt-text-2)" }}>
            Each domain hosts <code className="qt-mono text-xs">/.well-known/qortrace.json</code>
            {" "}pointing to their public radar — auto-discovered, no manual logo upload.
          </p>
        </div>
        <Link
          to="/signup"
          data-testid="customer-radar-showcase-cta"
          className="qt-mono text-xs inline-flex items-center gap-1"
          style={{
            padding: "10px 16px",
            borderRadius: 999,
            border: "1px solid var(--qt-cyan)",
            background: "rgba(0,229,255,0.10)",
            color: "var(--qt-cyan)",
            textDecoration: "none",
            letterSpacing: "0.10em",
          }}
        >
          ADD YOURS — FREE <ArrowRight size={11} />
        </Link>
      </div>

      <div
        className="mt-6 grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
      >
        {items.map((it) => {
          // Prefer domain when present; fall back to social handle as identity.
          const hasDomain = !!it.domain;
          const hasSocial = !!(it.social && it.social.handle);
          const primary   = hasDomain ? it.domain : (hasSocial ? `@${it.social.handle}` : it.slug);
          const subLabel  = hasDomain
            ? (it.cert_match ? "AUDIT CERT MATCH" : "DOMAIN VERIFIED")
            : (it.social.platform === "linkedin" ? "LINKEDIN VERIFIED" : "X / TWITTER VERIFIED");
          const subColor  = hasDomain && it.cert_match ? "var(--qt-green)" : "var(--qt-text-3)";
          // Social-only rows use a generic platform glyph; favicons only
          // exist for domains.
          const logo = hasDomain ? it.logo_url : null;
          return (
            <Link
              key={it.slug}
              to={`/customer/${it.slug}/threat-radar`}
              data-testid={`customer-radar-showcase-item-${it.slug}`}
              className="qt-card qt-card-interactive flex items-center gap-3 py-3 px-4"
              style={{ textDecoration: "none" }}
              title={it.title || primary}
            >
              {logo ? (
                <img
                  src={logo}
                  alt={`${primary} logo`}
                  width={32}
                  height={32}
                  loading="lazy"
                  style={{
                    borderRadius: 6,
                    background: "rgba(255,255,255,0.06)",
                    flexShrink: 0,
                  }}
                  onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                />
              ) : (
                <div
                  aria-hidden="true"
                  style={{
                    width: 32, height: 32, borderRadius: 6, flexShrink: 0,
                    border: "1px solid rgba(0,229,255,0.30)",
                    background: "rgba(0,229,255,0.06)",
                    color: "var(--qt-cyan)",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "var(--qt-mono, monospace)", fontSize: 11, fontWeight: 700,
                  }}
                >
                  {(it.social?.handle?.[0] || it.slug?.[0] || "?").toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div
                  className="qt-mono text-xs truncate"
                  style={{ color: "#fff" }}
                >
                  {primary}
                </div>
                <div
                  className="qt-mono text-[9px]"
                  style={{ color: subColor, letterSpacing: "0.10em" }}
                >
                  {subLabel}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
